/**
 * =============================================================================
 * USE RFI HOOK
 * =============================================================================
 *
 * Owns the RFI-list fetch + refetch and the five RFI workflow mutations
 * (create / link responses / unlink response / review item / cancel) for ONE
 * question.
 *
 * Reconciliation model (D-7): every mutation returns the full updated
 * `AdminRfiRequest`; the hook MERGES it into the list by `rfiId` (replace in
 * place; append on create) and keeps the list sorted `createdAt ASC` to mirror
 * the worker's `RfiService.getByQuestionId`. There is NO optimistic synthesis
 * and NO blind refetch after a mutation.
 *
 * This hook is part of PR G (BL-ADMIN-RFI-PANEL-001) — the staff-facing admin
 * surface over the already-shipped RFI backend (PR A–F). It mirrors the
 * defensive scaffolding of `useReplies` (unmount guard, stale-key guard, per-
 * action pending keys, toast integration, server-error preservation), and
 * additionally enforces:
 *   - a FULL `(questionId, token)` stale guard on every fetch/mutation result
 *     (a slow response from a previous question OR a rotated token can never
 *     overwrite the current view), and
 *   - SYNCHRONOUS per-action guards (a `pendingActionsRef` for link/review/
 *     unlink + an `isCreatingRef` for create) so two same-tick invocations issue
 *     exactly one API call (React state alone is async and can be double-read).
 *
 * =============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../api/client';
import { useToast } from './useToast';
import type {
  AdminRfiRequest,
  CreateRfiRequest,
  CreateRfiResponseLinkInput,
  RfiCancelReasonCode,
  RfiCancelStatusTransition,
  RfiCompletionStatusTransition,
  RfiItemReviewStatus,
  RfiOpenStatusTransition,
} from '@find-my-part/types';

/** A surfaced (non-no-op) status-transition reason — drives the inline notice (D-3). */
type SurfacedTransition =
  | RfiOpenStatusTransition
  | RfiCompletionStatusTransition
  | RfiCancelStatusTransition;

/** Review status the UI can SET — `OPEN` is system-derived and never offered. */
export type SettableReviewStatus = Exclude<RfiItemReviewStatus, 'OPEN'>;

/**
 * Options for the useRfi hook.
 */
export interface UseRfiOptions {
  /** Question ID to fetch RFIs for. */
  questionId: string;
  /** Auth token. */
  token: string;
  /** Whether to auto-fetch on mount (default true). */
  enabled?: boolean;
  /**
   * Invoked with the created RFI after a SUCCESSFUL create (PR4b — the
   * post-create image-attach affordance needs the new RFI's anchor
   * `replyId`, which the boolean contract discards). Called after the merge;
   * optional so existing callers are untouched.
   */
  onCreated?: (rfi: AdminRfiRequest) => void;
}

/**
 * Return value of the useRfi hook.
 */
export interface UseRfiResult {
  /** The question's RFI requests (createdAt ASC). */
  rfis: AdminRfiRequest[];
  /** Whether the RFI list is loading (initial fetch / refetch). */
  isLoading: boolean;
  /** Error message if the list fetch failed. */
  error: string | null;
  /**
   * Namespaced keys for in-flight per-target mutations:
   * `review:${rfiItemId}`, `link:${rfiId}`, `unlink:${rfiResponseId}`.
   * Create uses a synchronous singleton guard (not this set).
   */
  pendingActions: Set<string>;
  /**
   * The most recent SURFACED status transition (`APPLIED` / `CLEARED` /
   * `stale_lost` / `FAILED`) or `null`. Set ONLY for those reasons; silent
   * no-op reasons leave it `null` (D-3) so the panel can render an inline
   * notice whenever it is non-null without re-filtering.
   */
  lastStatusTransition: SurfacedTransition | null;
  /** Re-fetch the RFI list, replacing the cache (an explicit "Refresh RFIs"). */
  refetch: () => Promise<void>;
  /** Open a new RFI request. Resolves true on success (form should reset). */
  createRfi: (input: CreateRfiRequest) => Promise<boolean>;
  /** Link a reply's evidence to one or more items on an RFI. Resolves true on success. */
  linkResponses: (rfiId: string, input: CreateRfiResponseLinkInput) => Promise<boolean>;
  /** Unlink a single response from an RFI. */
  unlinkResponse: (rfiId: string, rfiResponseId: string) => Promise<void>;
  /** Set/reset an item's review status. */
  reviewItem: (
    rfiId: string,
    rfiItemId: string,
    reviewStatus: SettableReviewStatus
  ) => Promise<void>;
  /**
   * Cancel (withdraw) an RFI — a terminal transition. Resolves true on success
   * (so the panel can close an open picker for that RFI). The optional
   * `reasonCode` keeps the hook aligned with the API; the v1 UI passes none.
   */
  cancelRfi: (rfiId: string, reasonCode?: RfiCancelReasonCode) => Promise<boolean>;
}

/**
 * User-friendly fallback messages for common error codes. Used ONLY when the
 * server message is absent or the code is not one whose verbatim message we
 * prefer — the RFI 409/400 server messages carry the actionable remedy
 * ("reset its review to UNREVIEWED first", "link evidence first", …) so we
 * prefer them for INVALID_STATE_TRANSITION / VALIDATION_ERROR (§2a).
 */
const RFI_ERROR_MESSAGES: Record<string, string> = {
  NOT_FOUND: 'RFI not found.',
  INVALID_STATE_TRANSITION: 'That RFI action is not allowed right now.',
  VALIDATION_ERROR: 'Invalid RFI request.',
  UNAUTHORIZED: 'Session expired. Please refresh the page.',
  FORBIDDEN: 'You do not have permission to manage RFIs.',
};

/** RFI-specific FORBIDDEN message (worker enforces `RFI_MANAGE`). */
const RFI_FORBIDDEN_MESSAGE = 'You do not have permission to manage RFIs.';

/**
 * Resolve the user-facing message for an RFI API error. Prefers the server's
 * actionable message for `INVALID_STATE_TRANSITION` and `VALIDATION_ERROR`
 * (the RFI remedy lives there); maps `FORBIDDEN` to an RFI-specific string;
 * falls back to the friendly map otherwise.
 */
function resolveRfiErrorMessage(error: { code: string; message?: string }): string {
  if (error.code === 'FORBIDDEN') {
    return RFI_FORBIDDEN_MESSAGE;
  }
  const serverMessage = error.message?.trim();
  if (
    (error.code === 'INVALID_STATE_TRANSITION' || error.code === 'VALIDATION_ERROR') &&
    serverMessage
  ) {
    return serverMessage;
  }
  return RFI_ERROR_MESSAGES[error.code] ?? serverMessage ?? 'Something went wrong. Please try again.';
}

/** True for the only transition reasons the UI surfaces (D-3). */
function isSurfacedReason(reason: SurfacedTransition['reason']): boolean {
  return (
    reason === 'APPLIED' ||
    reason === 'CLEARED' ||
    reason === 'CLEARED_AFTER_CANCEL' ||
    reason === 'stale_lost' ||
    reason === 'FAILED'
  );
}

/** Compose the full stale-guard key for a (questionId, token) pair. */
function stateKeyOf(questionId: string, token: string): string {
  return `${questionId}::${token}`;
}

/**
 * Merge a single updated RFI into the list by `rfiId` (replace in place; append
 * when new), then sort by `createdAt ASC` to mirror `getByQuestionId` so order
 * never drifts after a mutation (D-7).
 */
export function mergeRfi(
  list: AdminRfiRequest[],
  updated: AdminRfiRequest
): AdminRfiRequest[] {
  const index = list.findIndex((r) => r.rfiId === updated.rfiId);
  const next =
    index === -1 ? [...list, updated] : list.map((r, i) => (i === index ? updated : r));
  return next.slice().sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Hook for managing a question's RFI requests + workflow mutations.
 *
 * @example
 * ```tsx
 * const { rfis, isLoading, createRfi, reviewItem } = useRfi({
 *   questionId: 'q_abc123',
 *   token: authToken,
 * });
 * ```
 */
export function useRfi({
  questionId,
  token,
  enabled = true,
  onCreated,
}: UseRfiOptions): UseRfiResult {
  const [rfis, setRfis] = useState<AdminRfiRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [lastStatusTransition, setLastStatusTransition] = useState<SurfacedTransition | null>(
    null
  );

  const { showSuccess, showError, showWarning } = useToast();

  // Unmount protection: prevents setState after the component is destroyed.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Synchronous in-flight guard for the singleton create. React state is async,
  // so two invocations in one tick could both read a stale flag; this ref flips
  // synchronously so the second is rejected before a duplicate POST.
  const isCreatingRef = useRef(false);

  // Synchronous mirror of `pendingActions` (link/review/unlink). The rendered
  // `pendingActions` STATE drives button-disabled UI, but two same-tick calls
  // could both read a stale state Set and both fire; this ref is the
  // authoritative guard so exactly one API call is issued per key.
  const pendingActionsRef = useRef<Set<string>>(new Set());

  // Track the active (questionId, token) so a stale fetch/mutation result can
  // never overwrite state after the operator switches questions OR the token
  // rotates. On change we RESET all hook state (clear rfis/error/pending/notice).
  const stateKeyRef = useRef(stateKeyOf(questionId, token));
  useEffect(() => {
    const stateKey = stateKeyOf(questionId, token);
    if (stateKeyRef.current !== stateKey) {
      stateKeyRef.current = stateKey;
      isCreatingRef.current = false;
      pendingActionsRef.current = new Set();
      setRfis([]);
      setError(null);
      setPendingActions(new Set());
      setLastStatusTransition(null);
    }
  }, [questionId, token]);

  /** Atomically claim a pending key; returns false if it is already in flight. */
  const tryAddPending = useCallback((key: string): boolean => {
    if (pendingActionsRef.current.has(key)) return false;
    pendingActionsRef.current.add(key);
    setPendingActions(new Set(pendingActionsRef.current));
    return true;
  }, []);

  /** Release a pending key (ref + state). */
  const removePending = useCallback((key: string): void => {
    if (pendingActionsRef.current.delete(key)) {
      setPendingActions(new Set(pendingActionsRef.current));
    }
  }, []);

  /** True iff a result captured at `startedStateKey` is still the active view. */
  const isResultCurrent = useCallback(
    (startedStateKey: string): boolean =>
      isMountedRef.current && stateKeyRef.current === startedStateKey,
    []
  );

  /**
   * Apply a create/review status transition: toast + inline-notice state. Sets
   * `lastStatusTransition` ONLY for surfaced reasons (else clears it to null),
   * so a silent no-op never renders a notice and a later no-op clears a stale
   * one (D-3).
   */
  const applyStatusTransition = useCallback(
    (transition: SurfacedTransition) => {
      if (transition.reason === 'APPLIED') {
        showSuccess('Question moved to Needs More Info.');
      } else if (transition.reason === 'CLEARED') {
        showSuccess('Question reopened — information received.');
      } else if (transition.reason === 'CLEARED_AFTER_CANCEL') {
        showSuccess('Question reopened — request withdrawn.');
      } else if (transition.reason === 'stale_lost') {
        showWarning(
          'Your RFI change was saved, but the question status changed elsewhere. Review the current question status before continuing.'
        );
      } else if (transition.reason === 'FAILED') {
        showWarning('RFI saved, but the question-status update did not apply.');
      }
      setLastStatusTransition(isSurfacedReason(transition.reason) ? transition : null);
    },
    [showSuccess, showWarning]
  );

  const fetchRfis = useCallback(async (): Promise<void> => {
    if (!token || !questionId) return;
    const startedStateKey = stateKeyOf(questionId, token);

    setIsLoading(true);
    setError(null);

    try {
      const response = await adminApi.rfi.list(token, questionId);
      if (!isResultCurrent(startedStateKey)) return;

      if (response.success) {
        // The list endpoint returns createdAt ASC; re-sort defensively.
        const ordered = response.data.rfis
          .slice()
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        setRfis(ordered);
      } else {
        setError(resolveRfiErrorMessage(response.error));
      }
    } catch {
      if (!isResultCurrent(startedStateKey)) return;
      setError('Failed to load RFIs. Please try again.');
    } finally {
      // Only clear loading if this result is still the active view, so a slow
      // prior-question/token fetch can't prematurely clear the new view's spinner.
      if (isResultCurrent(startedStateKey)) {
        setIsLoading(false);
      }
    }
  }, [token, questionId, isResultCurrent]);

  useEffect(() => {
    if (enabled) {
      fetchRfis();
    }
  }, [enabled, fetchRfis]);

  const refetch = useCallback(async (): Promise<void> => {
    await fetchRfis();
  }, [fetchRfis]);

  const createRfi = useCallback(
    async (input: CreateRfiRequest): Promise<boolean> => {
      // Singleton synchronous guard: reject a duplicate that races within a tick.
      if (isCreatingRef.current) return false;
      isCreatingRef.current = true;
      const startedStateKey = stateKeyOf(questionId, token);

      try {
        const response = await adminApi.rfi.create(token, questionId, input);
        if (!isResultCurrent(startedStateKey)) return false;

        if (response.success) {
          setRfis((prev) => mergeRfi(prev, response.data.rfi));
          showSuccess(response.data.message || 'RFI request opened.');
          applyStatusTransition(response.data.statusTransition);
          // PR4b: hand the created RFI (with its anchor replyId) to the panel
          // for the post-create image-attach affordance.
          onCreated?.(response.data.rfi);
          return true;
        }
        showError(resolveRfiErrorMessage(response.error));
        return false;
      } catch {
        if (!isResultCurrent(startedStateKey)) return false;
        showError('Failed to open the RFI. Please try again.');
        return false;
      } finally {
        // Release the singleton guard ONLY if this create is still the active
        // view. A stale create that resolves after a question/token switch must
        // not clear a fresh in-flight create's guard (the switch's reset effect
        // already cleared the guard for the new view).
        if (isResultCurrent(startedStateKey)) {
          isCreatingRef.current = false;
        }
      }
    },
    [token, questionId, isResultCurrent, showSuccess, showError, applyStatusTransition, onCreated]
  );

  const linkResponses = useCallback(
    async (rfiId: string, input: CreateRfiResponseLinkInput): Promise<boolean> => {
      const key = `link:${rfiId}`;
      if (!tryAddPending(key)) return false;
      const startedStateKey = stateKeyOf(questionId, token);

      try {
        const response = await adminApi.rfi.linkResponses(token, questionId, rfiId, input);
        if (!isResultCurrent(startedStateKey)) return false;

        if (response.success) {
          setRfis((prev) => mergeRfi(prev, response.data.rfi));
          showSuccess('Evidence linked.');
          return true;
        }
        showError(resolveRfiErrorMessage(response.error));
        return false;
      } catch {
        if (!isResultCurrent(startedStateKey)) return false;
        showError('Failed to link the response. Please try again.');
        return false;
      } finally {
        // Release the pending key ONLY if this op is still the active view, so a
        // stale op can't clear a fresh same-key claim made after a switch (the
        // switch's reset effect already cleared the key for the new view).
        if (isResultCurrent(startedStateKey)) removePending(key);
      }
    },
    [tryAddPending, removePending, token, questionId, isResultCurrent, showSuccess, showError]
  );

  const unlinkResponse = useCallback(
    async (rfiId: string, rfiResponseId: string): Promise<void> => {
      const key = `unlink:${rfiResponseId}`;
      if (!tryAddPending(key)) return;
      const startedStateKey = stateKeyOf(questionId, token);

      try {
        const response = await adminApi.rfi.unlinkResponse(token, questionId, rfiId, rfiResponseId);
        if (!isResultCurrent(startedStateKey)) return;

        if (response.success) {
          setRfis((prev) => mergeRfi(prev, response.data.rfi));
          showSuccess('Response unlinked.');
        } else {
          showError(resolveRfiErrorMessage(response.error));
        }
      } catch {
        if (!isResultCurrent(startedStateKey)) return;
        showError('Failed to unlink the response. Please try again.');
      } finally {
        // Release the pending key ONLY if this op is still the active view, so a
        // stale op can't clear a fresh same-key claim made after a switch (the
        // switch's reset effect already cleared the key for the new view).
        if (isResultCurrent(startedStateKey)) removePending(key);
      }
    },
    [tryAddPending, removePending, token, questionId, isResultCurrent, showSuccess, showError]
  );

  const reviewItem = useCallback(
    async (rfiId: string, rfiItemId: string, reviewStatus: SettableReviewStatus): Promise<void> => {
      const key = `review:${rfiItemId}`;
      if (!tryAddPending(key)) return;
      const startedStateKey = stateKeyOf(questionId, token);

      try {
        const response = await adminApi.rfi.reviewItem(token, questionId, rfiId, rfiItemId, {
          reviewStatus,
        });
        if (!isResultCurrent(startedStateKey)) return;

        if (response.success) {
          setRfis((prev) => mergeRfi(prev, response.data.rfi));
          showSuccess('Item review updated.');
          applyStatusTransition(response.data.statusTransition);
        } else {
          showError(resolveRfiErrorMessage(response.error));
        }
      } catch {
        if (!isResultCurrent(startedStateKey)) return;
        showError('Failed to update the review. Please try again.');
      } finally {
        // Release the pending key ONLY if this op is still the active view, so a
        // stale op can't clear a fresh same-key claim made after a switch (the
        // switch's reset effect already cleared the key for the new view).
        if (isResultCurrent(startedStateKey)) removePending(key);
      }
    },
    [
      tryAddPending,
      removePending,
      token,
      questionId,
      isResultCurrent,
      showSuccess,
      showError,
      applyStatusTransition,
    ]
  );

  const cancelRfi = useCallback(
    async (rfiId: string, reasonCode?: RfiCancelReasonCode): Promise<boolean> => {
      const key = `cancel:${rfiId}`;
      if (!tryAddPending(key)) return false;
      const startedStateKey = stateKeyOf(questionId, token);

      try {
        // Send no body when there is no reasonCode (the v1 UI passes none) — the
        // client omits the body entirely.
        const response = await adminApi.rfi.cancel(
          token,
          questionId,
          rfiId,
          reasonCode ? { reasonCode } : undefined
        );
        if (!isResultCurrent(startedStateKey)) return false;

        if (response.success) {
          // No optimistic removal — flip to read-only from the server-returned
          // CANCELLED RFI (merge by rfiId).
          setRfis((prev) => mergeRfi(prev, response.data.rfi));
          showSuccess('RFI cancelled.');
          applyStatusTransition(response.data.statusTransition);
          return true;
        }
        showError(resolveRfiErrorMessage(response.error));
        return false;
      } catch {
        if (!isResultCurrent(startedStateKey)) return false;
        showError('Failed to cancel the RFI. Please try again.');
        return false;
      } finally {
        // Release the pending key ONLY if this op is still the active view (same
        // stale-switch guard as the other mutations).
        if (isResultCurrent(startedStateKey)) removePending(key);
      }
    },
    [
      tryAddPending,
      removePending,
      token,
      questionId,
      isResultCurrent,
      showSuccess,
      showError,
      applyStatusTransition,
    ]
  );

  return {
    rfis,
    isLoading,
    error,
    pendingActions,
    lastStatusTransition,
    refetch,
    createRfi,
    linkResponses,
    unlinkResponse,
    reviewItem,
    cancelRfi,
  };
}
